import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Brain, Volume2, X, Plus } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const GrammarExplanationPanel = ({ 
  explanation, 
  selectedText,
  onClose,
  onAddToTraining,
  isVisible = false
}) => {
  const { toast } = useToast();

  if (!isVisible || !explanation) return null;

  const handleAddToTraining = () => {
    if (onAddToTraining) {
      onAddToTraining({
        type: 'grammar_explanation',
        title: explanation.name,
        text: selectedText,
        explanation: explanation.explanation,
        level: explanation.level
      });
    }
    
    toast({
      title: "Grammatik hinzugefügt",
      description: `"${explanation.name}" wurde zum Training hinzugefügt.`,
    });
  };

  const speakText = (text) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'de-DE';
      utterance.rate = 0.8;
      speechSynthesis.speak(utterance);
    }
  };

  const getLevelColor = (level) => {
    const colors = {
      'A1': 'bg-green-100 text-green-800',
      'A2': 'bg-blue-100 text-blue-800',
      'B1': 'bg-yellow-100 text-yellow-800',
      'B2': 'bg-orange-100 text-orange-800',
      'C1': 'bg-red-100 text-red-800'
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  return (
    <Card className="border-0 shadow-lg border-l-4 border-[#4ECDC4] mt-4">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Brain className="h-5 w-5 text-[#4ECDC4]" />
            <CardTitle className="text-lg font-bold">
              {explanation.name}
            </CardTitle>
          </div>
          <div className="flex items-center space-x-2">
            <Badge className={getLevelColor(explanation.level)}>
              {explanation.level}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Selected Text */}
        {selectedText && (
          <div className="bg-[#4ECDC4] bg-opacity-10 border-[#4ECDC4] border rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-slate-900 text-sm mb-1">Ausgewählter Text:</h4>
                <p className="text-[#4ECDC4] font-semibold">
                  "{selectedText}"
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => speakText(selectedText)}
              >
                <Volume2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Explanation */}
        <div>
          <h4 className="font-medium text-slate-900 mb-2">Erklärung:</h4>
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="text-slate-800 leading-relaxed text-sm">
              {explanation.explanation}
            </p>
          </div>
        </div>

        {/* Examples */}
        {explanation.examples && explanation.examples.length > 0 && (
          <div>
            <h4 className="font-medium text-slate-900 mb-2">Beispiele:</h4>
            <div className="space-y-2">
              {explanation.examples.map((example, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-blue-50 rounded text-sm border-l-2 border-blue-200">
                  <p className="text-slate-800 flex-1">
                    {example}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => speakText(example)}
                  >
                    <Volume2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Usage Tips */}
        <div className="bg-blue-50 p-3 rounded-lg">
          <h4 className="font-medium text-blue-900 text-sm mb-1">💡 Tipp:</h4>
          <p className="text-blue-800 text-xs">
            Klicken Sie auf weitere Textstellen in der Geschichte, um ähnliche Grammatikstrukturen zu entdecken.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t">
          <Button
            className="bg-[#4ECDC4] hover:bg-[#45b7aa] text-white text-sm"
            onClick={handleAddToTraining}
            size="sm"
          >
            <Plus className="h-3 w-3 mr-1" />
            Zum Training hinzufügen
          </Button>
          <Button
            variant="outline"
            onClick={onClose}
            size="sm"
            className="text-sm"
          >
            Schließen
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default GrammarExplanationPanel;